import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaTelegram, FaFacebook, FaTiktok, FaSnapchatGhost, FaLinkedin, FaInstagram } from 'react-icons/fa';
import logoSrc from '@/assets/snap-chart-logo-final.png';
import { useDesignVersion } from '@/hooks/useDesignVersion';

const { FiArrowRight, FiShield, FiZap, FiLock, FiActivity } = FiIcons;

const Footer = () => {
  const { version: designVersion } = useDesignVersion();
  const isV3 = designVersion === 'v3';
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (email) {
      alert(`Thanks — we'll send updates to ${email}.`);
      e.target.reset();
    }
  };

  const socialLinks = [
    { icon: () => <span className="font-black text-base leading-none">𝕏</span>, href: 'https://twitter.com/snapchart_ai', label: 'X' },
    { icon: FaInstagram, href: 'https://instagram.com/snapchartai', label: 'Instagram' },
    { icon: FaTiktok, href: 'https://tiktok.com/@snapchartai', label: 'TikTok' },
    { icon: FaTelegram, href: 'https://t.me/snapchartai', label: 'Telegram' },
    { icon: FaLinkedin, href: 'https://linkedin.com/company/snapchart-ai', label: 'LinkedIn' },
    { icon: FaFacebook, href: 'https://facebook.com/snapchartai', label: 'Facebook' },
    { icon: FaSnapchatGhost, href: 'https://snapchat.com/add/snapchartai', label: 'Snapchat' },
  ];

  const productLinks = [
    { name: 'How it works', anchor: '/#how' },
    { name: 'Pricing', anchor: '/#pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'Free test', path: '/test' },
    { name: 'Leaderboard', path: '/leaderboard' },
    { name: 'Affiliate · 30%', path: '/affiliate' },
  ];

  const accountLinks = [
    { name: 'Log in', path: '/login' },
    { name: 'Sign up free', path: '/register' },
    { name: 'Forgot password', path: '/forgot-password' },
  ];

  const legalLinks = [
    { name: 'Terms', path: '/terms' },
    { name: 'Privacy', path: '/privacy' },
    { name: 'Risk Disclaimer', path: '/risk-disclaimer' },
    { name: 'Cookies', path: '/cookies' },
  ];

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-black border-t border-white/5">
      {/* Top gradient hairline */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Top — big viral CTA band (V3 ONLY).
            V1 mode shows the original Footer without the 'Get the AI that says NO.' band. */}
        {isV3 && (
          <div className="relative rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/5 via-gray-900/40 to-purple-500/5 p-6 md:p-8 mb-12 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 mb-3">
                  still here? · still scrolling?
                </span>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight mb-1">
                  Get the AI <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">that says NO.</span>
                </h3>
                <p className="text-sm text-gray-400">30 days free · no credit card · zero broker connection</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Link to="/register"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold hover:scale-[1.02] transition shadow-xl shadow-cyan-500/20 whitespace-nowrap">
                  Start free trial <SafeIcon icon={FiArrowRight} />
                </Link>
                <Link to="/test"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-gray-200 hover:bg-white/5 transition whitespace-nowrap">
                  Run the free test
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Main grid — 12 cols */}
        <div className="grid grid-cols-12 gap-8 mb-12">
          {/* Brand block */}
          <div className="col-span-12 md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3 mb-5 group">
              <img src={logoSrc} alt="SnapChart AI" className="h-10 w-10 transition group-hover:scale-110" />
              {isV3 ? (
                <span className="text-xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  SnapChart AI
                </span>
              ) : (
                <span className="text-xl font-bold text-white">SnapChart AI</span>
              )}
            </Link>
            <p className="text-gray-300 leading-relaxed mb-4 max-w-md">
              The first AI Risk Officer that vetoes your worst trades — <span className="text-cyan-400 font-semibold">before</span> you take them.
            </p>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md">
              Built-in chart + one-click capture. Or drag any screenshot from MT4, MT5, TradingView, every crypto exchange. We never touch your broker.
            </p>

            {/* Trust strip — V3 ONLY (the LIVE / ENCRYPTED / NO BROKER API / VERDICT IN 2S
                mono badges are part of the viral v3 visual language). */}
            {isV3 && (
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-mono uppercase tracking-[0.15em] text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                  live
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <SafeIcon icon={FiLock} className="text-cyan-400" /> encrypted
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <SafeIcon icon={FiShield} className="text-cyan-400" /> no broker api
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <SafeIcon icon={FiZap} className="text-cyan-400" /> verdict in 2s
                </span>
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="hidden md:block md:col-span-1"></div>

          {/* Section labels: V3 mono uppercase tracking, V1 classic capitalized */}
          {/* Product */}
          <div className="col-span-6 md:col-span-2">
            <h4 className={isV3
              ? "text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 mb-4"
              : "text-sm font-semibold text-gray-300 mb-4"}>
              {isV3 ? 'product' : 'Product'}
            </h4>
            <ul className="space-y-2.5">
              {productLinks.map((l) => (
                <li key={l.name}>
                  {l.anchor ? (
                    <a href={l.anchor} className="text-sm text-gray-300 hover:text-cyan-400 transition">{l.name}</a>
                  ) : (
                    <Link to={l.path} className="text-sm text-gray-300 hover:text-cyan-400 transition">{l.name}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div className="col-span-6 md:col-span-2">
            <h4 className={isV3
              ? "text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 mb-4"
              : "text-sm font-semibold text-gray-300 mb-4"}>
              {isV3 ? 'account' : 'Account'}
            </h4>
            <ul className="space-y-2.5">
              {accountLinks.map((l) => (
                <li key={l.name}>
                  <Link to={l.path} className="text-sm text-gray-300 hover:text-cyan-400 transition">{l.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="col-span-12 md:col-span-2">
            <h4 className={isV3
              ? "text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 mb-4"
              : "text-sm font-semibold text-gray-300 mb-4"}>
              {isV3 ? 'stay close' : 'Stay close'}
            </h4>
            <p className="text-xs text-gray-400 mb-3 leading-relaxed">Product updates, no spam.</p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <input
                type="email"
                name="email"
                placeholder={isV3 ? 'you@email' : 'your@email.com'}
                required
                className="w-full px-3 py-2.5 bg-gray-900/60 border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 transition"
              />
              <button
                type="submit"
                className={isV3
                  ? "w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 hover:bg-cyan-500/5 rounded-lg text-xs font-mono uppercase tracking-[0.15em] text-cyan-300 transition"
                  : "w-full px-3 py-2.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-semibold text-white transition"}
              >
                {isV3 ? 'Subscribe →' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* Social row — V3 'follow' mono uppercase, V1 'Follow us' regular */}
        <div className="flex flex-wrap items-center gap-2 mb-10">
          <span className={isV3
            ? "text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 mr-2"
            : "text-sm text-gray-400 mr-2"}>
            {isV3 ? 'follow' : 'Follow us'}
          </span>
          {socialLinks.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
              className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 hover:bg-cyan-500/5 hover:text-cyan-400 transition text-gray-400 flex items-center justify-center">
              {typeof s.icon === 'function' && s.icon.length === 0 ? <s.icon /> : <SafeIcon icon={s.icon} className="text-base" />}
            </a>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-gray-500">
          <div className={`flex items-center gap-4 ${isV3 ? 'font-mono' : ''}`}>
            <span>© {new Date().getFullYear()} <span className="text-gray-300">SnapChart AI</span></span>
            {isV3 && (
              <>
                <span className="hidden md:inline text-gray-700">·</span>
                <span className="hidden md:inline">Building in public.</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {legalLinks.map((l) => (
              <Link key={l.name} to={l.path} className="hover:text-cyan-400 transition">
                {l.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Tiny system note — V3 ONLY (mono uppercase pulse is part of viral language) */}
        {isV3 && (
          <p className="text-center text-[10px] font-mono uppercase tracking-[0.2em] text-gray-700 mt-6">
            <SafeIcon icon={FiActivity} className="inline mr-1.5 text-cyan-700" />
            ai is not financial advice · always do your own research
          </p>
        )}
      </div>
    </footer>
  );
};

export default Footer;
